// Govern360 Console - top-level component.
// Manual routing off window.location.pathname (CT360 parity).
// CHANGE (multi-org): self-signup Onboarding removed. Authenticated users with
// no org see NoOrgAccess (invite/domain/request), not a create-org form.

import { useEffect, useState, lazy, Suspense } from 'react';
import {
  AuthProvider,
  useAuth,
  LoadingScreen,
  LoginPage,
  AuthCallback,
  AcceptInvitePage,
} from '@govern360/shared';
import NoOrgAccess from './NoOrgAccess.jsx';
import ConsoleApp from './ConsoleApp.jsx';
import MarketplaceLandingPage from './pages/MarketplaceLandingPage.jsx';
import { ToastProvider, ConfirmProvider } from '@govern360/shared';

/* G360_K836 - lazy, matching how ConsoleApp loads it, so the main console
   bundle does not grow for a route only the detached window uses. */
const HelpSupportPage = lazy(() => import('./pages/HelpSupportPage.jsx'));

/* Drive the opener, not this window. A step that says "go to Endpoint
   Governance" should move the console the person is working in and leave
   these instructions on screen. If this window was opened directly rather
   than by the console, fall back to a normal tab. */
function navigateOpener(r) {
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.location.hash = '#' + r;
      window.opener.focus();
      return;
    }
  } catch (e) { /* cross-origin or closed; fall through */ }
  window.open('/#' + r, '_blank', 'noopener,noreferrer');
}

const LOGIN_CONFIG = {
  variant: 'console',
  title: 'Sign in to your console',
  subtitle: "Enter your work email to continue. We'll route you to your organization.",
  ssoButtons: ['google', 'azure'],
  emailHint: 'you@company.com',
  footerNode: (
    <>
      Internal staff?{' '}
      <a href="https://admin.govern360.ai">Sign in to admin console {'\u2192'}</a>
      <br />
      {/* G360_K520 - the public AI news site, also in the console menu (Latest AI News). */}
      <a href="https://news.govern360.ai" target="_blank" rel="noopener noreferrer">Latest AI news {'\u2192'}</a>
    </>
  ),
};

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider><ConfirmProvider>
        <AppContent />
      </ConfirmProvider></ToastProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const { isAuthenticated, hasOrg, loading } = useAuth();
  const [path, setPath] = useState(
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Public routes (no auth required, no shell).
  if (path === '/auth/callback') return <AuthCallback />;
  if (path === '/accept-invite') return <AcceptInvitePage />;
  // Marketplace purchase setup. Public entry point: the buyer arrives here from
  // Azure/AWS/GCP and may never have signed in. Stash the handoff so it survives
  // the OAuth round trip, then require auth + an org before binding anything.
  if (path === '/marketplace/landing') {
    if (typeof window !== 'undefined') {
      const h = new URLSearchParams(window.location.search).get('handoff');
      if (h) sessionStorage.setItem('g360_marketplace_handoff', h);
    }
    if (loading) return <LoadingScreen label="Checking your session..." />;
    if (!isAuthenticated) return <LoginPage config={LOGIN_CONFIG} />;
    if (!hasOrg) return <NoOrgAccess />;
    return <MarketplaceLandingPage />;
  }

  if (loading) return <LoadingScreen label={'Checking your session\u2026'} />;

  // Not signed in -> login.
  if (!isAuthenticated) return <LoginPage config={LOGIN_CONFIG} />;

  // Signed in but no organization -> request/invite screen (NOT self-signup).
  if (!hasOrg) return <NoOrgAccess />;

  /* G360_K836 - the detached Support Expert window. Signed in and in an org
     like any console page, but deliberately rendered WITHOUT the shell: it
     is a 460px window meant to sit beside the console, not a second copy of
     it. `navigate` drives the window that opened it, so acting on a step in
     an answer moves the console the person is actually working in, and this
     little window stays put with the instructions still on screen. */
  if (path === '/support') {
    return (
      <Suspense fallback={<LoadingScreen label={'Opening the support expert…'} />}>
        <HelpSupportPage popout navigate={navigateOpener} />
      </Suspense>
    );
  }

  // Signed in + has an org -> main app.
  // Resume an interrupted marketplace setup after login.
  if (typeof window !== 'undefined') {
    const pending = sessionStorage.getItem('g360_marketplace_handoff');
    if (pending) {
      sessionStorage.removeItem('g360_marketplace_handoff');
      window.location.replace('/marketplace/landing?handoff=' + encodeURIComponent(pending));
      return <LoadingScreen label="Returning to your purchase..." />;
    }
  }
  return <ConsoleApp />;
}
